import { AxiosPromise, AxiosRequestConfig } from 'axios';
import { IsimpleMeasurementPointResult } from '../models';
declare function postMessage(message: any): void;
declare function importScripts(...urls: string[]): void;
declare function axios(url: string, config?: AxiosRequestConfig): AxiosPromise;
declare const localforage: LocalForage;

export default () => {
    const root = process.env.REACT_APP_SERVER_DOMAIN;
    const host = process.env.REACT_APP_HOST_DOMAIN;
    
    // Inline type definitions (can't import in worker context)
    interface MaterialStreamDto {
        id?: string;
        sku?: string;
        num?: string;
        name: string;
        desc?: string;
        sap?: boolean;
        type: number;
        date: string;
        del?: boolean;
        meas?: string;
        instTime?: number;
        partType?: number;
        pgc?: string;
        subCatId?: string;
        brandId?: string;
        prodTypeId?: string;
        powerId?: string;
        sysSizeId?: string;
        isFinal?: boolean;
        approved?: boolean;
        compliant?: boolean;
        hasSpares?: boolean;
        notes?: string;
        commMethod?: number;
        mergedId?: string;
        imgPath?: string;
        links?: Array<{
            id: string;
            url: string;
            type: number;
            other: string;
        }>;
        standards?: string[];
    }

    interface IMaterial {
        id: string;
        sku: string;
        number: string;
        name: string;
        description?: string;
        sap: boolean;
        type: number;
        date: string;
        del?: boolean;
        measurement?: string;
        installationTime?: number;
        partType?: number;
        pgc?: string;
        subcategoryID?: string;
        brandID?: string;
        productTypeID?: string;
        powerID?: string;
        systemSizeID?: string;
        isFinalProduct?: boolean;
        isApproved?: boolean;
        isCompliant?: boolean;
        hasSpares?: boolean;
        communicationMethod?: number;
        mergedMaterialID?: string;
        imagePath?: string;
        links?: Array<{
            id: string;
            url: string;
            type: number;
            other: string;
        }>;
        standards?: string[];
    }

    function normalizeMaterialResponse(data: unknown): MaterialStreamDto[] {
        if (!data) return [];
        if (Array.isArray(data)) return data as MaterialStreamDto[];
        if (typeof data === 'object') {
            return Object.entries(data).map(([id, material]) => ({ 
                id, 
                ...(material as MaterialStreamDto) 
            }));
        }
        return [];
    }

    // Converts MaterialStreamDto (abbreviated API format) to IMaterial (full property names)
    function streamDtoToMaterial(dto: MaterialStreamDto): IMaterial {
        const material: IMaterial = {
            id: dto.id || '',
            sku: dto.sku || '',
            number: dto.num || '',
            name: dto.name,
            sap: dto.sap || false,
            type: dto.type,
            date: dto.date
        };

        // Only include optional properties if they have values
        if (dto.desc !== undefined) material.description = dto.desc;
        if (dto.del !== undefined) material.del = dto.del;
        
        // Part-specific
        if (dto.meas !== undefined) material.measurement = dto.meas;
        if (dto.instTime !== undefined) material.installationTime = dto.instTime;
        if (dto.partType !== undefined) material.partType = dto.partType;
        
        // Product-specific
        if (dto.pgc !== undefined) material.pgc = dto.pgc;
        if (dto.subCatId !== undefined) material.subcategoryID = dto.subCatId;
        if (dto.brandId !== undefined) material.brandID = dto.brandId;
        if (dto.prodTypeId !== undefined) material.productTypeID = dto.prodTypeId;
        if (dto.powerId !== undefined) material.powerID = dto.powerId;
        if (dto.sysSizeId !== undefined) material.systemSizeID = dto.sysSizeId;
        if (dto.isFinal !== undefined) material.isFinalProduct = dto.isFinal;
        if (dto.approved !== undefined) material.isApproved = dto.approved;
        if (dto.compliant !== undefined) material.isCompliant = dto.compliant;
        if (dto.hasSpares !== undefined) material.hasSpares = dto.hasSpares;
        if (dto.commMethod !== undefined) material.communicationMethod = dto.commMethod;
        if (dto.mergedId !== undefined) material.mergedMaterialID = dto.mergedId;
        if (dto.imgPath !== undefined) material.imagePath = dto.imgPath;
        if (dto.links !== undefined) material.links = dto.links;
        if (dto.standards !== undefined) material.standards = dto.standards;

        return material;
    }

    // eslint-disable-next-line no-restricted-globals
    self.addEventListener('message', async e => {
        if (!e) return;

        importScripts(`${host}/axios.min.js`);
        importScripts(`${host}/localforage.min.js`);

        switch (e.data.action) {
            // MMG-3788: Removed legacy 'getAllProducts' case that called inventory/products
            // All product loading now uses 'getPagedProducts' with material/stream endpoint
            case 'GetPagedMeasurementPointListResultsForJob': {
                // Confirm if this is still going to be used or not
                const axiosOptions: AxiosRequestConfig = {
                    headers: {
                        Authorization: `Bearer ${e.data.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    },
                    method: 'post',
                    // data: [e.data.jobId],
                    timeout: 1000 * 60 * 5
                };

                if (e.data.jobId && e.data.page && e.data.batchAmount) {
                    const URL = `${root}/MeasurementPoint/GetPagedMeasurementPointListResultsForJob?JobID=${e.data.jobId}&page=${e.data.page}&petPage=${e.data.batchAmount}`;
                    axios(URL, axiosOptions)
                        .then(resp => {
                            postMessage({
                                action: 'getResultsSuccess',
                                results: resp.data,
                                facilityID: e.data.facilityID
                            });
                        })
                        .catch(error => {
                            postMessage({ action: 'getResultsFailed', error });
                        });
                }

                break;
            }
            case 'getSimpleJobMeasurementPointResults': {
                const axiosOptions: AxiosRequestConfig = {
                    headers: {
                        Authorization: `Bearer ${e.data.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    },
                    method: 'get',
                    params: {},
                    timeout: 1000 * 60 * 5
                };

                let allPromises: Array<Promise<void>> = [];
                let allResults: IsimpleMeasurementPointResult[] = [];
                if (e.data.jobIDs) {
                    e.data.jobIDs.forEach((jobID: string) => {
                        const URL = `${root}/MeasurementPoint/GetSimpleMeasurementPointListResults?JobID=${jobID}`;
                        allPromises.push(
                            axios(URL, axiosOptions).then(resp => {
                                allResults = allResults.concat(resp.data);
                            })
                        );
                    });
                    return Promise.all(allPromises)
                        .then(status => {
                            postMessage({
                                action: 'getSimpleResultsSuccess',
                                results: allResults,
                                jobIDs: e.data.jobIDs
                            });
                        })
                        .catch(error => {
                            postMessage({ action: 'getSimpleResultsFailed', error });
                        });
                }
                break;
            }
            case 'getPagedProducts': {
                const axiosOptions: AxiosRequestConfig = {
                    headers: {
                        Authorization: `Bearer ${e.data.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    },
                    method: 'GET',
                    timeout: 600_000 // 10 minutes
                };
                const URL = `${root}/material/stream?numChunks=${e.data.numChunks}&chunkIndex=${e.data.chunkIndex}`;

                // MMG-4461: progress trail for this chunk, read back on the next launch if the page dies; best effort only
                const diagStartedAt = Date.now();
                const diag = async (stage: string, extra?: Record<string, unknown>) => {
                    try {
                        // Object.assign, not spread: this function is stringified into the worker and has no compiler helpers
                        await localforage.setItem(
                            `med-gas-mobile_product-sync-diag_c${e.data.chunkIndex}`,
                            Object.assign({ stage, elapsedMs: Date.now() - diagStartedAt }, extra || {})
                        );
                    } catch (diagError) {
                        console.warn('[ProductSyncDiagnostics]: chunk write skipped', diagError);
                    }
                };
                // Size of the raw JSON already held in memory - no extra copy is made
                const responseChars = (resp: any): number | undefined => {
                    try {
                        const text = resp?.request?.responseText;
                        return typeof text === 'string' ? text.length : undefined;
                    } catch {
                        return undefined;
                    }
                };

                try {
                    await diag('requested');
                    const resp = await axios(URL, axiosOptions);
                    const responseData = resp.data.result ? resp.data.result : resp.data;
                    await diag('received', {
                        items: Array.isArray(responseData) ? responseData.length : undefined,
                        responseChars: responseChars(resp)
                    });

                    const rawMaterials = normalizeMaterialResponse(responseData);
                    const allMaterials: IMaterial[] = rawMaterials.map(dto => streamDtoToMaterial(dto));
                    await diag('parsed', { items: allMaterials.length });

                    if (allMaterials.length > 0) {
                        const mStore = localforage.createInstance({
                            name: 'my-med-gas-mobile',
                            storeName: 'materials'
                        });

                        const updateDateStr = await localforage.getItem('med-gas-mobile_all-products_delta');
                        let mostRecentDate = updateDateStr ? new Date(updateDateStr as string) : new Date(0);

                        postMessage({
                            action: 'updateProductRecordCount',
                            totalProducts: allMaterials.length,
                            chunkIndex: e.data.chunkIndex,
                            numChunks: e.data.numChunks
                        });

                        // Zone-less API dates are UTC; read them that way (inline copy of utils/serverDate - the worker cannot import)
                        const parseServerDate = (value: string): number => {
                            let text = String(value).trim().replace(/(\.\d{3})\d+/, '$1');
                            if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(text)) text += 'Z';
                            return Date.parse(text);
                        };
                        allMaterials.forEach(material => {
                            if (material.date) {
                                const t = parseServerDate(material.date);
                                if (Number.isFinite(t) && t > mostRecentDate.getTime()) {
                                    mostRecentDate = new Date(t);
                                }
                            }
                        });

                        await mStore.ready();

                        const db = await new Promise<IDBDatabase>((resolve, reject) => {
                            const request = indexedDB.open('my-med-gas-mobile');
                            request.onsuccess = () => resolve(request.result);
                            request.onerror = () => reject(request.error);
                        });

                        const transaction = db.transaction(['materials'], 'readwrite');
                        const objectStore = transaction.objectStore('materials');

                        allMaterials.forEach(material => {
                            objectStore.put(material, material.id);
                        });

                        await new Promise<void>((resolve, reject) => {
                            transaction.oncomplete = () => resolve();
                            transaction.onerror = () => reject(transaction.error);
                        });

                        db.close();
                        await diag('stored', { items: allMaterials.length });

                        postMessage({
                            action: 'updateProductProgress',
                            recordsProcessed: allMaterials.length,
                            totalRecords: allMaterials.length,
                            chunkIndex: e.data.chunkIndex,
                            numChunks: e.data.numChunks
                        });

                        await localforage.setItem('med-gas-mobile_all-products_delta', mostRecentDate.toISOString());
                    }

                    postMessage({ action: 'getPagedProductsSuccess', chunkIndex: e.data.chunkIndex });
                } catch (error) {
                    console.error('Error getting paged products: ', error);
                    await diag('failed', { error: String((error as any)?.message || error).slice(0, 500) });
                    postMessage({ action: 'getPagedProductsFailed', error, chunkIndex: e.data.chunkIndex });
                }

                break;
            }
            default:
                break;
        }
    });
};
